import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { DurchfuehrungsTag } from '@chancenwerk/_cms/products/entities/Product.js';
import { ProductLKK } from '@chancenwerk/_cms/products/entities/ProductLKK.js';
import { Weekday } from '@chancenwerk/db/enums/enums.js';
import type { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { DateTime } from 'luxon';
import { Course } from './Course.js';

/**
 * Defines if the course sync is allowed to create new courses.
 * This is disabled for now but can be enabled if needed.
 */
const ALLOW_COURSE_CREATION = false;

function sameHourMinute(a: Maybe<DateTime>, b: Maybe<DateTime>) {
  if (!a || !b) {
    return true;
  }
  return a.hasSame(b, 'hour') && a.hasSame(b, 'minute');
}

function weekdayTimeKey(w: Maybe<Weekday>, start: Maybe<DateTime>) {
  return `${w ?? 'UNKNOWN'}${start?.toFormat('HH:mm') ?? 'XX:XX'}`;
}

interface OperationAdded {
  n: DurchfuehrungsTag;
}

interface OperationRemoved {
  n: DurchfuehrungsTag;
}

interface OperationChanged {
  o: DurchfuehrungsTag;
  n: DurchfuehrungsTag;
  roomsAdded: string[];
  roomsRemoved: string[];
}

export enum CourseLernfoerderungVersion {
  V1 = 'V1',
  V2 = 'V2',
}

/**
 * @entity CourseLernfoerderung
 */
@Entity()
export class CourseLernfoerderung extends Course {
  public static override _class = 'CourseLernfoerderung';

  @Field(type => String)
  version: Maybe<CourseLernfoerderungVersion>;

  public static async updateAndCreateCoursesOfKPF(kpf: KPF<ProductLKK>, oldKPF?: KPF<ProductLKK>) {
    if (!kpf.produkt || kpf.produkt?._class !== ProductLKK._class) return;
    if (!oldKPF?.produkt || oldKPF.produkt?._class !== ProductLKK._class) return;

    const useV2 = kpf.produkt?._class === ProductLKK._class && kpf.produkt.useV2Courses;

    // * If two durchfuehrungsTage have the same weekday and startTime we need to abort
    // ! This is a hard restriction so that we don't lose sanity
    const duplicateCheck = new Set<string>();
    for (const day of kpf.produkt.durchfuehrungsTage) {
      if (!day.cid) {
        throw new Error('cid is missing in durchfuehrungsTage');
      }

      const key = weekdayTimeKey(day.wochentag, day.startZeit);
      if (duplicateCheck.has(key)) {
        throw new Error(`Duplicate weekday and startTime found in durchfuehrungsTage: ${key}`);
      }
      duplicateCheck.add(key);
    }

    // * Get list of all courses from the kpf
    const courseCollection = await getRawCollection<EntityData<CourseLernfoerderung>>(
      CourseLernfoerderung._collectionName,
    );
    const courses: CourseLernfoerderung[] = (
      await courseCollection
        .find({
          kpfID: kpf._id,
          _class: CourseLernfoerderung._class,
          ...(useV2
            ? { version: CourseLernfoerderungVersion.V2 }
            : { version: { $ne: CourseLernfoerderungVersion.V2 } }),
        })
        .toArray()
    ).map(c => constructValue(c) as CourseLernfoerderung);

    // * Add list of changed courses
    const coursesToUpdate: CourseLernfoerderung[] = [];
    const coursesToRemove: CourseLernfoerderung[] = [];

    // * If there are no courses we can just create them from the new data
    if (courses.length === 0) {
      if (!ALLOW_COURSE_CREATION) {
        return;
      }

      kpf.produkt.durchfuehrungsTage.forEach(day => {
        day.locations.forEach(location => {
          coursesToUpdate.push(
            new CourseLernfoerderung({
              kpfID: kpf._id,
              startTime: day.startZeit,
              endTime: day.endZeit,
              weekday: day.wochentag,
              location,
            }),
          );
        });
      });

      for (const course of coursesToUpdate) {
        await course.create();
      }

      return;
    }

    // * Create a map of courses by weekday and startTime e.g. { "Montag14:30": [ ... ], ... }
    const keyedCoursesBefore: Record<string, CourseLernfoerderung[]> = {};
    courses.forEach(c => {
      const key = weekdayTimeKey(c.weekday, c.startTime);
      if (!keyedCoursesBefore[key]) keyedCoursesBefore[key] = [];
      keyedCoursesBefore[key]?.push(c);
    });

    // * Create a set of the current durchfuehrungsTage by weekday and startTime e.g. [ "Montag14:30", ... ]
    const keyedCoursesNow = new Set<string>();
    kpf.produkt.durchfuehrungsTage.forEach(c => {
      const key = weekdayTimeKey(c.wochentag, c.startZeit);
      if (!keyedCoursesNow[key]) keyedCoursesNow[key] = [];
      keyedCoursesNow.add(key);
    });

    //
    // Compare phase: Check for added, removed and changed courses
    //

    const added: OperationAdded[] = [];
    const removed: OperationRemoved[] = [];
    const changed: OperationChanged[] = [];

    // * Check for added
    for (const day of kpf.produkt.durchfuehrungsTage) {
      const old = oldKPF.produkt.durchfuehrungsTage.find(d => d.cid === day.cid);
      if (!old) {
        // * If the course should be added but we have a course with the same weekday and startTime in the old data we need to abort
        // * This avoids duplicate course creation in case the cid was changed but the course is still the same
        const key = weekdayTimeKey(day.wochentag, day.startZeit);
        if (keyedCoursesNow[key]) {
          continue;
        }

        added.push({ n: day });
        continue;
      }
    }

    // * Check for removed
    for (const day of oldKPF.produkt.durchfuehrungsTage) {
      const newDay = kpf.produkt.durchfuehrungsTage.find(d => d.cid === day.cid);
      if (!newDay) {
        // * If the course should be removed but we have a course with the same weekday and startTime in the new data we need to abort
        // * This avoids course removal in case the cid was changed but the course is still the same
        const key = weekdayTimeKey(day.wochentag, day.startZeit);
        if (keyedCoursesNow[key]) {
          continue;
        }

        removed.push({ n: day });
        continue;
      }
    }

    // * Check for changed
    for (const day of kpf.produkt.durchfuehrungsTage) {
      const old = oldKPF.produkt.durchfuehrungsTage.find(d => d.cid === day.cid);
      if (!old) continue;

      const roomsAdded = day.locations.filter(l => !old.locations.includes(l));
      const roomsRemoved = old.locations.filter(l => !day.locations.includes(l));
      if (
        !sameHourMinute(old.startZeit, day.startZeit) ||
        !sameHourMinute(old.endZeit, day.endZeit) ||
        old.wochentag !== day.wochentag ||
        roomsAdded.length > 0 ||
        roomsRemoved.length > 0
      ) {
        changed.push({ o: old, n: day, roomsAdded, roomsRemoved });
      }
    }

    //
    // Aplying operation phase
    //

    // * Remove courses
    for (const { n } of removed) {
      const key = weekdayTimeKey(n.wochentag, n.startZeit);
      coursesToRemove.push(...(keyedCoursesBefore[key] ?? []));
    }

    // * Create courses
    if (ALLOW_COURSE_CREATION) {
      for (const { n } of added) {
        n.locations.forEach(location => {
          coursesToUpdate.push(
            new CourseLernfoerderung({
              kpfID: kpf._id,
              startTime: n.startZeit,
              endTime: n.endZeit,
              weekday: n.wochentag,
              location,
            }),
          );
        });
      }
    }

    // * Update courses
    for (const { o, n, roomsAdded, roomsRemoved } of changed) {
      const key = weekdayTimeKey(o.wochentag, o.startZeit);
      const coursesToUpdateForKey = keyedCoursesBefore[key] ?? [];
      for (const course of coursesToUpdateForKey) {
        course.startTime = n.startZeit;
        course.endTime = n.endZeit;
        course.weekday = n.wochentag;
        coursesToUpdate.push(course);
      }

      if (ALLOW_COURSE_CREATION) {
        if (roomsAdded.length > 0) {
          for (const location of roomsAdded) {
            if (coursesToUpdateForKey.find(c => c.location === location)) continue;

            coursesToUpdate.push(
              new CourseLernfoerderung({
                kpfID: kpf._id,
                startTime: n.startZeit,
                endTime: n.endZeit,
                weekday: n.wochentag,
                location,
              }),
            );
          }
        }
      }

      // ! disable remove for now!
      // if (roomsRemoved.length > 0) {
      //   for (const location of roomsRemoved) {
      //     coursesToRemove.push(...coursesToUpdateForKey.filter(c => c.location === location));
      //   }
      // }

      if (ALLOW_COURSE_CREATION) {
        // * Create missing courses as sanity check
        const rooms = n.locations.filter(l => !roomsAdded.includes(l) && !roomsRemoved.includes(l));
        for (const location of rooms) {
          if (coursesToUpdateForKey.find(c => c.location === location)) continue;

          coursesToUpdate.push(
            new CourseLernfoerderung({
              kpfID: kpf._id,
              startTime: n.startZeit,
              endTime: n.endZeit,
              weekday: n.wochentag,
              location,
            }),
          );
        }
      }
    }

    // * Save courses
    for (const course of coursesToUpdate) {
      // ! delay because multiple saves seem to have a timing problem
      await new Promise<void>((resolve, reject) => {
        setTimeout(() => {
          resolve();
        }, 100);
      });

      if (!course._id) await course.create();
      await course.save();
    }

    // * Remove courses
    for (const course of coursesToRemove) {
      await course.remove();
    }
  }

  static override async beforeCreate(course: CourseLernfoerderung) {
    if (course.kpfID) {
      const similarCourses = await loadMongoData(CourseLernfoerderung, {
        _class: CourseLernfoerderung._class,
        kpfID: course.kpfID,
        weekday: course.weekday,
        location: course.location,
      });

      if (
        similarCourses.some(c => {
          return (
            DateTime.fromJSDate(c.startTime ?? new Date()).hasSame(course.startTime ?? DateTime.now(), 'hour') &&
            DateTime.fromJSDate(c.startTime ?? new Date()).hasSame(course.startTime ?? DateTime.now(), 'minute')
          );
        })
      ) {
        throw new Error('A course with the same location and weekday already exists at this time');
      }
    }

    await super.beforeCreate(course);
  }

  static isCourseCreationAllowed() {
    return ALLOW_COURSE_CREATION;
  }
}
